import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Building2, CheckCircle2, Loader2, Pencil, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { toast } from '../../lib/dialogs'
import {
  type InvoiceKindT,
  type InvoiceSeriesDto,
  type IssuerDto,
} from '@jr2os/shared'
import { useAuth } from '../../contexts/AuthContext'
import { useTableSort } from '../../lib/useTableSort'

export default function BillingSettingsPage() {
  const { t } = useTranslation('settings')
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'ADMIN'
  const [series, setSeries] = useState<InvoiceSeriesDto[]>([])
  const [issuers, setIssuers] = useState<IssuerDto[]>([])
  const [loading, setLoading] = useState(true)
  const [showNew, setShowNew] = useState(false)
  const [issuerModal, setIssuerModal] = useState<{ open: boolean; issuer: IssuerDto | null }>({
    open: false,
    issuer: null,
  })
  const [error, setError] = useState<string | null>(null)

  // Orden por columna de las dos tablas, en la URL (página entera) y cada una con su `id` para que no se pisen.
  // Las cabeceras no llevan relleno horizontal (como antes): `py-px` es el 1px que ya ponía el navegador a las celdas sin clase.
  const { rows: sortedIssuers, header: issuerHeader } = useTableSort(
    issuers,
    {
      name: (i) => i.name,
      nif: (i) => i.nif,
      default: (i) => i.isDefault,
      status: (i) => (i.active ? t('billing.status.active') : t('billing.status.inactive')),
    },
    { id: 'issuers' }
  )
  const { rows: sortedSeries, header: seriesHeader } = useTableSort(
    series,
    {
      prefix: (s) => s.prefix,
      name: (s) => s.name,
      issuer: (s) => s.issuerName,
      kind: (s) => t('enums:InvoiceKind.' + s.kind),
      default: (s) => s.isDefault,
      status: (s) => (s.active ? t('billing.status.activeF') : t('billing.status.inactiveF')),
    },
    { id: 'series' }
  )

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const [s, i] = await Promise.all([
        api.get<{ items: InvoiceSeriesDto[] }>('/invoice-series'),
        api.get<{ items: IssuerDto[] }>('/issuers'),
      ])
      setSeries(s.items)
      setIssuers(i.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const onMakeDefault = async (id: string) => {
    try {
      await api.patch(`/invoice-series/${id}`, { isDefault: true })
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error')
    }
  }
  const onToggleActive = async (s: InvoiceSeriesDto) => {
    try {
      await api.patch(`/invoice-series/${s.id}`, { active: !s.active })
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error')
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-serif font-semibold t-primary">{t('billing.title')}</h2>
        <p className="t-muted text-sm mt-1">
          {t('billing.subtitle')}
        </p>
      </header>

      {/* ===== Emisores (F19) ===== */}
      <section className="surface p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="t-primary font-semibold flex items-center gap-2">
            <Building2 size={15} className="text-jr2-700 dark:text-jr2-300" /> {t('billing.issuers')}
          </h3>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setIssuerModal({ open: true, issuer: null })}
              className="px-2.5 py-1 rounded-lg text-xs font-medium text-jr2-700 dark:text-jr2-200 bg-jr2-500/15 hover:bg-jr2-500/25 flex items-center gap-1"
            >
              <Plus size={12} /> {t('billing.newIssuer')}
            </button>
          )}
        </div>
        {issuers.length === 0 ? (
          <p className="text-sm t-faint py-3 text-center">{t('billing.noIssuers')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider t-faint">
              <tr>
                {issuerHeader('name', t('billing.col.name'), { className: 'py-1.5' })}
                {issuerHeader('nif', t('billing.col.nif'), { className: 'py-px' })}
                {issuerHeader('default', t('billing.col.default'), { align: 'center', className: 'py-px' })}
                {issuerHeader('status', t('billing.col.status'), { align: 'center', className: 'py-px' })}
                {isAdmin && <th className="w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {sortedIssuers.map((i) => (
                <tr key={i.id} className="border-t border-base/40">
                  <td className="py-2 t-primary">
                    {i.name}
                    {i.legalName && (
                      <span className="block text-xs t-faint">{i.legalName}</span>
                    )}
                  </td>
                  <td className="py-2 font-mono t-secondary">{i.nif || '—'}</td>
                  <td className="py-2 text-center">
                    {i.isDefault && <CheckCircle2 size={14} className="inline text-emerald-400" />}
                  </td>
                  <td className="py-2 text-center text-xs">
                    <span className={i.active ? 'text-emerald-700 dark:text-emerald-300' : 't-faint'}>
                      {i.active ? t('billing.status.active') : t('billing.status.inactive')}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        onClick={() => setIssuerModal({ open: true, issuer: i })}
                        className="t-faint hover:text-jr2-700 dark:hover:text-jr2-200"
                        title={t('billing.editIssuer')}
                      >
                        <Pencil size={13} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="text-xs t-faint mt-2">
          {t('billing.issuersFootnote')}
        </p>
      </section>

      {/* ===== Series ===== */}
      <section className="surface p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="t-primary font-semibold">{t('billing.series')}</h3>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowNew(true)}
              className="px-2.5 py-1 rounded-lg text-xs font-medium text-jr2-700 dark:text-jr2-200 bg-jr2-500/15 hover:bg-jr2-500/25 flex items-center gap-1"
            >
              <Plus size={12} /> {t('billing.newSeries')}
            </button>
          )}
        </div>

        {error && <p className="text-xs text-rose-700 dark:text-rose-300 mb-3">{error}</p>}

        {loading ? (
          <div className="t-muted flex items-center gap-2 text-sm py-3">
            <Loader2 size={14} className="animate-spin" /> {t('billing.loading')}
          </div>
        ) : series.length === 0 ? (
          <p className="text-sm t-faint py-3 text-center">{t('billing.noSeries')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider t-faint">
              <tr>
                {seriesHeader('prefix', t('billing.col.prefix'), { className: 'py-1.5' })}
                {seriesHeader('name', t('billing.col.name'), { className: 'py-px' })}
                {seriesHeader('issuer', t('billing.col.issuer'), { className: 'py-px' })}
                {seriesHeader('kind', t('billing.col.kind'), { className: 'py-px' })}
                {seriesHeader('default', t('billing.col.default'), { align: 'center', className: 'py-px' })}
                {seriesHeader('status', t('billing.col.status'), { align: 'center', className: 'py-px' })}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedSeries.map((s) => (
                <tr key={s.id} className="border-t border-base/40">
                  <td className="py-2 font-mono text-jr2-700 dark:text-jr2-300">{s.prefix}</td>
                  <td className="py-2 t-primary">{s.name}</td>
                  <td className="py-2 t-secondary text-xs">{s.issuerName}</td>
                  <td className="py-2 t-secondary text-xs">{t('enums:InvoiceKind.' + s.kind)}</td>
                  <td className="py-2 text-center">
                    {s.isDefault ? (
                      <CheckCircle2 size={14} className="inline text-emerald-400" />
                    ) : isAdmin ? (
                      <button
                        type="button"
                        onClick={() => void onMakeDefault(s.id)}
                        className="text-xs t-faint hover:text-jr2-700 dark:hover:text-jr2-200"
                      >
                        {t('billing.markDefault')}
                      </button>
                    ) : (
                      <span className="t-faint">—</span>
                    )}
                  </td>
                  <td className="py-2 text-center">
                    {isAdmin ? (
                      <button
                        type="button"
                        onClick={() => void onToggleActive(s)}
                        className={`text-xs px-2 py-0.5 rounded border ${
                          s.active
                            ? 'border-emerald-500/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10'
                            : 'border-base t-faint'
                        }`}
                      >
                        {s.active ? t('billing.status.activeF') : t('billing.status.inactiveF')}
                      </button>
                    ) : (
                      <span>{s.active ? t('billing.status.activeF') : t('billing.status.inactiveF')}</span>
                    )}
                  </td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {showNew && (
        <NewSeriesModal
          issuers={issuers.filter((i) => i.active)}
          onClose={() => setShowNew(false)}
          onSaved={async () => {
            setShowNew(false)
            await reload()
          }}
        />
      )}

      {issuerModal.open && (
        <IssuerModal
          issuer={issuerModal.issuer}
          onClose={() => setIssuerModal({ open: false, issuer: null })}
          onSaved={async () => {
            setIssuerModal({ open: false, issuer: null })
            await reload()
          }}
        />
      )}
    </div>
  )
}

function NewSeriesModal({
  issuers,
  onClose,
  onSaved,
}: {
  issuers: IssuerDto[]
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const { t } = useTranslation('settings')
  const [prefix, setPrefix] = useState('')
  const [name, setName] = useState('')
  const [kind, setKind] = useState<InvoiceKindT>('ORDINARIA')
  const [issuerId, setIssuerId] = useState(
    issuers.find((i) => i.isDefault)?.id ?? issuers[0]?.id ?? ''
  )
  const [isDefault, setIsDefault] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!prefix.trim() || !name.trim() || !issuerId) {
      setError(t('billing.newSeriesModal.errorRequired'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.post('/invoice-series', { prefix, name, kind, isDefault, issuerId })
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md surface-deep p-5 animate-slideUp space-y-3"
      >
        <h3 className="text-base font-semibold t-primary">{t('billing.newSeriesModal.title')}</h3>

        <label className="block">
          <span className="text-xs t-muted">{t('billing.newSeriesModal.issuerLabel')}</span>
          <select
            value={issuerId}
            onChange={(e) => setIssuerId(e.target.value)}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
            required
          >
            {issuers.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} {i.nif ? `(${i.nif})` : ''}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">{t('billing.col.prefix')}</span>
            <input
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              maxLength={8}
              placeholder={t('billing.newSeriesModal.prefixPlaceholder')}
              className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5 font-mono"
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('billing.col.kind')}</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as InvoiceKindT)}
              className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
            >
              {(['ORDINARIA', 'RECTIFICATIVA', 'PROFORMA'] as const).map((k) => (
                <option key={k} value={k}>
                  {t('enums:InvoiceKind.' + k)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-xs t-muted">{t('billing.col.name')}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('billing.newSeriesModal.namePlaceholder')}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
            required
          />
        </label>

        <label className="flex items-center gap-2 text-sm t-secondary">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            className="accent-jr2-500"
          />
          {t('billing.newSeriesModal.useAsDefault')}
        </label>

        {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            {t('billing.actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {t('billing.actions.create')}
          </button>
        </div>
      </form>
    </div>
  )
}

function IssuerModal({
  issuer,
  onClose,
  onSaved,
}: {
  issuer: IssuerDto | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const { t } = useTranslation('settings')
  const [form, setForm] = useState({
    name: issuer?.name ?? '',
    legalName: issuer?.legalName ?? '',
    nif: issuer?.nif ?? '',
    address: issuer?.address ?? '',
    city: issuer?.city ?? '',
    postalCode: issuer?.postalCode ?? '',
    province: issuer?.province ?? '',
    email: issuer?.email ?? '',
    phone: issuer?.phone ?? '',
    bankIban: issuer?.bankIban ?? '',
    bankBic: issuer?.bankBic ?? '',
    invoiceFootnote: issuer?.invoiceFootnote ?? '',
  })
  const [isDefault, setIsDefault] = useState(issuer?.isDefault ?? false)
  const [active, setActive] = useState(issuer?.active ?? true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const upd = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!form.name.trim() || !form.nif.trim()) {
      setError(t('billing.issuerModal.errorRequired'))
      return
    }
    setBusy(true)
    setError(null)
    const payload = {
      name: form.name.trim(),
      legalName: form.legalName.trim() || null,
      nif: form.nif.trim(),
      address: form.address.trim() || null,
      city: form.city.trim() || null,
      postalCode: form.postalCode.trim() || null,
      province: form.province.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      bankIban: form.bankIban.trim() || null,
      bankBic: form.bankBic.trim() || null,
      invoiceFootnote: form.invoiceFootnote.trim() || null,
      isDefault,
    }
    try {
      if (issuer) {
        await api.patch(`/issuers/${issuer.id}`, { ...payload, active })
      } else {
        await api.post('/issuers', payload)
      }
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  const inputCls =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5'

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4 overflow-y-auto py-8"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl surface-deep p-5 animate-slideUp space-y-3"
      >
        <h3 className="text-base font-semibold t-primary">
          {issuer ? t('billing.issuerModal.editTitle', { name: issuer.name }) : t('billing.issuerModal.newTitle')}
        </h3>
        <p className="text-xs t-faint">
          {t('billing.issuerModal.subtitle')}
        </p>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">{t('billing.issuerModal.commercialName')}</span>
            <input value={form.name} onChange={upd('name')} className={inputCls} required />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('billing.issuerModal.nifRequired')}</span>
            <input value={form.nif} onChange={upd('nif')} className={`${inputCls} font-mono`} required />
          </label>
          <label className="block col-span-2">
            <span className="text-xs t-muted">{t('billing.issuerModal.legalName')}</span>
            <input value={form.legalName} onChange={upd('legalName')} className={inputCls} />
          </label>
          <label className="block col-span-2">
            <span className="text-xs t-muted">{t('billing.issuerModal.address')}</span>
            <input value={form.address} onChange={upd('address')} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('billing.issuerModal.postalCode')}</span>
            <input value={form.postalCode} onChange={upd('postalCode')} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('billing.issuerModal.city')}</span>
            <input value={form.city} onChange={upd('city')} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('billing.issuerModal.province')}</span>
            <input value={form.province} onChange={upd('province')} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('billing.issuerModal.email')}</span>
            <input type="email" value={form.email} onChange={upd('email')} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('billing.issuerModal.phone')}</span>
            <input value={form.phone} onChange={upd('phone')} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('billing.issuerModal.iban')}</span>
            <input value={form.bankIban} onChange={upd('bankIban')} className={`${inputCls} font-mono`} />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('billing.issuerModal.bic')}</span>
            <input value={form.bankBic} onChange={upd('bankBic')} className={`${inputCls} font-mono`} />
          </label>
          <label className="block col-span-2">
            <span className="text-xs t-muted">{t('billing.issuerModal.footnote')}</span>
            <textarea
              rows={2}
              value={form.invoiceFootnote}
              onChange={upd('invoiceFootnote')}
              className={inputCls}
            />
          </label>
        </div>

        <div className="flex items-center gap-5">
          <label className="flex items-center gap-2 text-sm t-secondary">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="accent-jr2-500"
            />
            {t('billing.issuerModal.defaultIssuer')}
          </label>
          {issuer && (
            <label className="flex items-center gap-2 text-sm t-secondary">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="accent-jr2-500"
              />
              {t('billing.status.active')}
            </label>
          )}
        </div>

        {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            {t('billing.actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {t('billing.actions.save')}
          </button>
        </div>
      </form>
    </div>
  )
}
